import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  Send,
  X,
  Maximize2,
  Minimize,
  Loader2,
  Sparkles,
  Brain,
  Zap,
} from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import {
  sendAskReimagineChat,
  startAskReimagineAssistant,
} from "../../services/askReimagineApi";
import type {
  AskReimagineHistoryMessage,
  AskReimagineSuggestionType,
  AskReimagineUnitSession,
  AxlPilotChatContext,
} from "../../types/axlPilotContext";
import {
  buildAskReimagineAnswers,
  buildAskReimagineChatRequest,
  buildAskReimagineStartRequest,
} from "../../utils/buildAskReimagineRequests";
import {
  applyAssistantTurn,
  createEmptyAskReimagineSession,
  loadAskReimagineUnitSession,
  saveAskReimagineUnitSession,
} from "../../utils/askReimagineSessionStore";
import type { AssessmentFormUiConfig } from "../../utils/assessmentFormConfig";
import { cn } from "../../utils/cn";
import "../../assets/css/axlpilot-theme.css";

type FormStateSlice = {
  currentState?: string;
  futureState?: string;
  currentStatusDescription?: string;
  futureStatusDescription?: string;
};

type CopilotProps = {
  chatContext: AxlPilotChatContext;
  userId: number | string;
  projectId: number | string;
  userName?: string;
  formState: FormStateSlice;
  formConfig: AssessmentFormUiConfig;
  assistantAvailable?: boolean;
};

const XyloIcon = ({
  size = 24,
  color = "currentColor",
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="4"
      y="6"
      width="16"
      height="14"
      rx="8"
      fill={color === "currentColor" ? "#0f6b66" : color}
      stroke={color === "currentColor" ? "#ffffff" : color}
      strokeWidth="0.5"
    />
    <circle
      cx="9"
      cy="12"
      r="2"
      fill={color === "currentColor" ? "#ffffff" : color}
      opacity="0.9"
    />
    <circle
      cx="15"
      cy="12"
      r="2"
      fill={color === "currentColor" ? "#ffffff" : color}
      opacity="0.9"
    />
    <path
      d="M9 16Q12 18 15 16"
      stroke={color === "currentColor" ? "#ffffff" : color}
      strokeWidth="1.5"
      strokeLinecap="round"
      fill="none"
      opacity="0.8"
    />
    <line
      x1="12"
      y1="6"
      x2="12"
      y2="2"
      stroke={color === "currentColor" ? "#ffffff" : color}
      strokeWidth="1.5"
      strokeLinecap="round"
      opacity="0.7"
    />
    <circle
      cx="12"
      cy="2"
      r="1"
      fill={color === "currentColor" ? "#ffffff" : color}
      opacity="0.6"
    />
    <rect
      x="10"
      y="20"
      width="4"
      height="2"
      fill={color === "currentColor" ? "#ffffff" : color}
      opacity="0.5"
    />
  </svg>
);

function historyToDisplayMessages(history: AskReimagineHistoryMessage[]) {
  return history.map((entry, index) => ({
    id: `${entry.role}-${index}`,
    sender: entry.role === "user" ? ("user" as const) : ("assistant" as const),
    text: entry.content,
  }));
}

function getAxlPilotUnitLabel(context: AxlPilotChatContext): string {
  const unit = context.assessment.assessmentUnits.find(
    (entry) =>
      entry.capabilityId === context.capability_id &&
      (context.sub_capability_id == null
        ? entry.subCapabilityId == null
        : entry.subCapabilityId === context.sub_capability_id)
  );
  if (!unit) return "this section";
  const subName = unit.subCapabilityName?.trim();
  if (subName) return subName;
  return unit.capabilityName?.trim() || "this section";
}

const Copilot = ({
  chatContext,
  userId,
  projectId,
  userName,
  formState,
  formConfig,
  assistantAvailable = true,
}: CopilotProps) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [session, setSession] = useState<AskReimagineUnitSession>(() =>
    createEmptyAskReimagineSession()
  );
  const [inputMessage, setInputMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(
    null
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatContextRef = useRef(chatContext);
  const formStateRef = useRef(formState);
  const formConfigRef = useRef(formConfig);
  const sessionRef = useRef(session);
  const startInFlightRef = useRef(false);

  chatContextRef.current = chatContext;
  formStateRef.current = formState;
  formConfigRef.current = formConfig;
  sessionRef.current = session;

  const capabilityId = chatContext.capability_id;
  const subCapabilityId = chatContext.sub_capability_id;

  const getUsername = () => user?.userName || userName || "User";

  const persistSession = useCallback(
    (next: AskReimagineUnitSession) => {
      setSession(next);
      sessionRef.current = next;
      saveAskReimagineUnitSession(
        userId,
        projectId,
        capabilityId,
        subCapabilityId,
        next
      );
    },
    [userId, projectId, capabilityId, subCapabilityId]
  );

  useEffect(() => {
    const stored =
      loadAskReimagineUnitSession(
        userId,
        projectId,
        capabilityId,
        subCapabilityId
      ) ?? createEmptyAskReimagineSession();
    setSession(stored);
    sessionRef.current = stored;
    startInFlightRef.current = false;
    setErrorMessage(null);
    setInputMessage("");
    setPendingUserMessage(null);
  }, [userId, projectId, capabilityId, subCapabilityId]);

  const syncAxlpilotInputLayout = (target: HTMLTextAreaElement) => {
    const shell = target.parentElement;
    if (!shell?.classList.contains("axlpilot-input-shell")) return;

    target.style.height = "auto";
    const nextHeight = Math.max(20, Math.min(target.scrollHeight, 96));
    target.style.height = `${nextHeight}px`;

    const isMultiline = nextHeight > 28 || target.value.includes("\n");
    shell.classList.toggle("axlpilot-input-shell--multiline", isMultiline);
  };

  const displayMessages = historyToDisplayMessages(session.history);
  if (pendingUserMessage) {
    displayMessages.push({
      id: "pending-user",
      sender: "user",
      text: pendingUserMessage,
    });
  }
  const unitLabel = getAxlPilotUnitLabel(chatContext);
  const showInitialLoading =
    isOpen &&
    isStarting &&
    displayMessages.length === 0 &&
    !pendingUserMessage;
  const showWelcome =
    isOpen &&
    !session.started &&
    !isStarting &&
    displayMessages.length === 0 &&
    !pendingUserMessage;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [session.history, pendingUserMessage, isSending, isStarting, errorMessage]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (inputRef.current) {
      syncAxlpilotInputLayout(inputRef.current);
    }
  }, [inputMessage]);

  const applyTurnResponse = (
    userMessage: string | null,
    reply: string,
    suggestions: string[],
    suggestionType: AskReimagineSuggestionType
  ) => {
    const next = applyAssistantTurn(
      sessionRef.current,
      userMessage,
      reply,
      suggestions,
      suggestionType
    );
    persistSession(next);
  };

  const runStart = useCallback(async () => {
    if (startInFlightRef.current || sessionRef.current.started) {
      return;
    }

    if (!assistantAvailable) {
      setErrorMessage("The assistant is unavailable right now.");
      return;
    }

    startInFlightRef.current = true;
    setIsStarting(true);
    setErrorMessage(null);

    try {
      const response = await startAskReimagineAssistant(
        buildAskReimagineStartRequest(chatContextRef.current)
      );
      applyTurnResponse(
        null,
        response.reply,
        response.suggestions ?? [],
        response.suggestion_type === "answers" ? "answers" : "next_steps"
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Could not start assistant.";
      setErrorMessage(message);
    } finally {
      startInFlightRef.current = false;
      setIsStarting(false);
    }
  }, [assistantAvailable, persistSession]);

  useEffect(() => {
    if (!isOpen || session.started || isStarting) {
      return;
    }
    void runStart();
  }, [isOpen, session.started, isStarting, runStart]);

  const sendUserMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isSending || isStarting) return;

    if (!assistantAvailable) {
      setErrorMessage("The assistant is unavailable right now.");
      return;
    }

    setErrorMessage(null);
    setInputMessage("");
    setPendingUserMessage(trimmed);
    setIsSending(true);

    const historyForRequest = [...sessionRef.current.history];

    try {
      const answers = buildAskReimagineAnswers(
        formStateRef.current,
        formConfigRef.current
      );
      const response = await sendAskReimagineChat(
        buildAskReimagineChatRequest(
          chatContextRef.current,
          trimmed,
          answers,
          historyForRequest
        )
      );

      applyTurnResponse(
        trimmed,
        response.reply,
        response.suggestions ?? [],
        response.suggestion_type === "answers" ? "answers" : "next_steps"
      );
      setPendingUserMessage(null);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Could not send message.";
      setErrorMessage(message);
      setPendingUserMessage(null);
    } finally {
      setIsSending(false);
    }
  };

  const toggleCopilot = () => {
    if (isOpen) {
      setIsOpen(false);
      setIsFullScreen(false);
      return;
    }
    setIsOpen(true);
  };

  const renderSuggestions = () => {
    if (!session.suggestions.length || isSending || isStarting) {
      return null;
    }

    const isAnswers = session.suggestion_type === "answers";

    return (
      <div className="axlpilot-suggestions">
        {isAnswers ? (
          <p className="axlpilot-suggestions__label">Quick answers</p>
        ) : null}
        <div
          className={cn(
            "axlpilot-suggestions__list",
            isAnswers && "axlpilot-suggestions__list--chips"
          )}
        >
          {session.suggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              className={cn(
                "axlpilot-suggestion-btn",
                isAnswers && "axlpilot-suggestion-btn--chip"
              )}
              disabled={isSending || isStarting}
              onClick={() => void sendUserMessage(suggestion)}
            >
              {suggestion}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={cn("axlpilot-toggle-wrap", isOpen && "is-hidden")}>
        <button
          type="button"
          onClick={toggleCopilot}
          className="axlpilot-toggle-btn"
          aria-label="Open axlPILOT assistant"
        >
          <XyloIcon size={20} color="#ffffff" />
          <span>axlPILOT</span>
        </button>
      </div>

      <div
        className={cn(
          "axlpilot-panel",
          isOpen && "is-open",
          isFullScreen && "is-fullscreen"
        )}
        aria-hidden={!isOpen}
      >
        <div className="axlpilot-header">
          <div className="axlpilot-header__brand">
            <div className="axlpilot-header__icon">
              <XyloIcon size={24} color="#0f6b66" />
            </div>
            <div>
              <h3>axlPILOT</h3>
              <p>Ask about this assessment area</p>
            </div>
          </div>
          <div className="axlpilot-header__actions">
            <button
              type="button"
              onClick={() => setIsFullScreen((prev) => !prev)}
              className="axlpilot-header-btn"
              title={isFullScreen ? "Exit full screen" : "Full screen"}
            >
              {isFullScreen ? <Minimize size={20} /> : <Maximize2 size={20} />}
            </button>
            <button
              type="button"
              onClick={toggleCopilot}
              className="axlpilot-header-btn"
              aria-label="Close axlPILOT"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {!assistantAvailable && isOpen ? (
          <div className="axlpilot-error axlpilot-error--banner">
            <span>The assistant may be unavailable. You can still try again.</span>
          </div>
        ) : null}

        {errorMessage ? (
          <div className="axlpilot-error">
            <span>{errorMessage}</span>
            <button type="button" onClick={() => setErrorMessage(null)}>
              Dismiss
            </button>
          </div>
        ) : null}

        <div className="axlpilot-messages">
          {showInitialLoading ? (
            <div className="axlpilot-start-loading" role="status" aria-live="polite">
              <Loader2 size={20} className="axlpilot-spin" aria-hidden />
              <p>
                Loading axlPILOT for <strong>{unitLabel}</strong>…
              </p>
            </div>
          ) : showWelcome ? (
            <div className="axlpilot-welcome">
              <div className="axlpilot-welcome__logo">
                <XyloIcon size={40} color="#0f6b66" />
              </div>
              <h2>
                Welcome to{" "}
                <span className="axlpilot-welcome__brand-accent">axlPILOT</span>
                , {getUsername()}
              </h2>
              <p>Starting your assistant for this section…</p>
              <div className="axlpilot-welcome__features">
                <div className="axlpilot-feature">
                  <Sparkles size={18} aria-hidden />
                  <span>Smart analysis</span>
                </div>
                <div className="axlpilot-feature">
                  <Brain size={18} aria-hidden />
                  <span>AI insights</span>
                </div>
                <div className="axlpilot-feature">
                  <Zap size={18} aria-hidden />
                  <span>Quick answers</span>
                </div>
              </div>
            </div>
          ) : (
            <>
              {displayMessages.map((message) => {
                const isUser = message.sender === "user";
                return (
                  <div
                    key={message.id}
                    className={cn(
                      "axlpilot-msg-row",
                      isUser ? "axlpilot-msg-row--user" : "axlpilot-msg-row--bot"
                    )}
                  >
                    {isUser ? (
                      <div className="axlpilot-msg-user">{message.text}</div>
                    ) : (
                      <div className="axlpilot-msg-bot axlpilot-msg-bot--md">
                        <ReactMarkdown>{message.text}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                );
              })}

              {isSending && (
                <div className="axlpilot-msg-row axlpilot-msg-row--bot">
                  <div className="axlpilot-typing">
                    <div className="axlpilot-typing__inner">
                      <XyloIcon size={20} color="#0f6b66" />
                      <span>axlPILOT is thinking…</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {renderSuggestions()}

        <div className="axlpilot-input-area">
          <div className="axlpilot-input-row">
            <div className="axlpilot-input-shell">
              <textarea
                ref={inputRef}
                className="axlpilot-input"
                value={inputMessage}
                onChange={(e) => {
                  setInputMessage(e.target.value);
                  syncAxlpilotInputLayout(e.target);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void sendUserMessage(inputMessage);
                  }
                }}
                placeholder="Type your message…"
                rows={1}
                disabled={isSending || isStarting || !session.started}
              />
            </div>
            <button
              type="button"
              onClick={() => void sendUserMessage(inputMessage)}
              disabled={
                !inputMessage.trim() ||
                isSending ||
                isStarting ||
                !session.started
              }
              className="axlpilot-send-btn"
              aria-label="Send message"
            >
              {isSending ? (
                <Loader2 size={20} className="axlpilot-spin" />
              ) : (
                <Send size={20} />
              )}
            </button>
          </div>
          <p className="axlpilot-input-hint">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </>
  );
};

export default Copilot;
